import { getPermissionFunction } from 'hooks/usePermissions.jsx';
import { READ, PERMISSIONS } from 'shared-permissions';

const {
  QUEUES_MANAGEMENT,
  CALLS_OPENING_HOURS,
  CALL_SETTINGS,
  CALL_STATS,
  CALL_STATS_LEADERBOARD,
  CALLS_AGENT_TOOL,
  CALL_LIST,
  INTEGRATIONS_ONE_TO_ONE,
  EDIT_USERS_ONE_TO_ONE,
  WORKSPACE_SETTINGS,
  LOCALIZATION_ONE_TO_ONE,
} = PERMISSIONS;
import {
  ORG_AGENT_TOOL,
  ORG_CALL_BACKGROUND,
  ORG_CALL_LIST,
  ORG_CALL_INSIGHTS,
  ORG_CALL_STATS,
  ORG_INTEGRATIONS,
  ORG_CALL_CONFIGURATIONS,
  ORG_GROUPS,
  ORG_QUEUES,
  ORG_THEMES,
  ORG_LOCALIZATION,
  ORG_USERS,
  ORG_CALL_BOOKINGS,
  ORG_CALL_BOOKING_SERVICES,
  ORG_CALL_AGENT_AVAILABILITY,
  ORG_ONE_TO_ONE_HOME,
  ORG_ONE_TO_ONE_THEMES,
  ORG_CALL_BOOKING_NOTIFICATIONS,
  ORG_GENERAL_SETTINGS,
  ORG_PRODUCTS,
} from 'constants/routes.js';

const appointmentsFilter = ({ authUser, userData, permissions }) => {
  const isSuperadmin = authUser.isSuperadmin;
  const isBookableResource = userData?.calendars?.nativeBookings;

  return (
    isSuperadmin ||
    (getPermissionFunction(permissions)(CALLS_AGENT_TOOL, READ) && isBookableResource) ||
    getPermissionFunction(permissions)(QUEUES_MANAGEMENT, READ)
  );
};

const availabilityFilter = ({ userData, permissions }) => {
  const isBookableResource = userData?.calendars?.nativeBookings;

  return getPermissionFunction(permissions)(CALLS_AGENT_TOOL, READ) && isBookableResource;
};

const bookingsFilter = ({ callsSettings }) => {
  return callsSettings?.bookingSystem === 'native';
};

const ONE_TO_ONE_ROUTES = [
  {
    title: 'sidebarComponent.homeTitle',
    icon: 'home',
    route: ORG_ONE_TO_ONE_HOME,
    requiredFeatures: [],
    requiredPermission: [QUEUES_MANAGEMENT, CALLS_AGENT_TOOL, EDIT_USERS_ONE_TO_ONE],
    dataTestId: 'homeMenuItem',
    allowSuperAdmin: true,
    exact: true,
    shownInMasquerade: false,
  },
  {
    title: 'sidebarComponent.statsTitle',
    icon: 'insights',
    route: ORG_CALL_STATS,
    requiredFeatures: [],
    requiredPermission: [CALL_STATS, CALL_STATS_LEADERBOARD],
    allowSuperAdmin: true,
  },
  {
    title: 'sidebarComponent.queuesTitle',
    icon: 'time',
    route: ORG_QUEUES,
    requiredFeatures: ['callsEnabled', 'queuesManagementAdminUIEnabled'],
    requiredPermission: [QUEUES_MANAGEMENT, CALLS_OPENING_HOURS],
    allowSuperAdmin: true,
  },
  {
    title: 'sidebarComponent.productsTitle',
    icon: 'bag',
    route: ORG_PRODUCTS,
    requiredFeatures: ['productCatalogProductsCallsEnabled'],
    requiredPermission: [INTEGRATIONS_ONE_TO_ONE, CALLS_AGENT_TOOL],
    dataTestId: 'productsMenuItem',
    allowSuperAdmin: true,
  },
  {
    title: 'sidebarComponent.agentToolTitle',
    icon: 'profile',
    route: ORG_AGENT_TOOL,
    url: '',
    requiredFeatures: ['callsEnabled'],
    requiredPermission: CALLS_AGENT_TOOL,
    allowSuperAdmin: true,
  },
  {
    title: 'sidebarComponent.callListTitle',
    icon: 'phone',
    route: ORG_CALL_LIST,
    requiredFeatures: ['callsEnabled', 'callListEnabled'],
    requiredPermission: CALL_LIST,
    allowSuperAdmin: true,
    customFilterFunction: ({ features }) => !features.callsTranscriptionsEnabled,
  },
  {
    title: 'sidebarComponent.callsTitle',
    icon: 'phone',
    submenu: [
      {
        title: 'sidebarComponent.callListTitle',
        route: ORG_CALL_LIST,
        requiredFeatures: ['callsEnabled', 'callListEnabled'],
        requiredPermission: CALL_LIST,
        allowSuperAdmin: true,
      },
      {
        title: 'callConfigurationsPage.transcriptionsTitle',
        route: ORG_CALL_INSIGHTS,
        requiredFeatures: ['callsEnabled', 'callListEnabled', 'callsTranscriptionsEnabled'],
        requiredPermission: [CALL_LIST],
        allowSuperAdmin: true,
      },
    ],
    customFilterFunction: ({ features }) => features.callsTranscriptionsEnabled,
  },
  {
    title: 'Bookings',
    icon: 'list',
    requiredFeatures: ['callsEnabled', 'callsBookingsEnabled'],
    customFilterFunction: bookingsFilter,
    submenu: [
      {
        title: 'Appointments',
        route: ORG_CALL_BOOKINGS,
        requiredPermission: [QUEUES_MANAGEMENT, CALLS_AGENT_TOOL],
        customFilterFunction: appointmentsFilter,
        allowSuperAdmin: true,
      },
      {
        title: 'Services',
        route: ORG_CALL_BOOKING_SERVICES,
        requiredPermission: [QUEUES_MANAGEMENT],
        allowSuperAdmin: true,
      },
      {
        title: 'My availability',
        route: ORG_CALL_AGENT_AVAILABILITY,
        requiredPermission: [CALLS_AGENT_TOOL],
        customFilterFunction: availabilityFilter,
      },
      {
        title: 'Email template',
        route: ORG_CALL_BOOKING_NOTIFICATIONS,
        requiredPermission: [QUEUES_MANAGEMENT],
        allowSuperAdmin: true,
      },
    ],
  },
  {
    title: 'sidebarComponent.settingsTitle',
    icon: 'settings',
    align: 'bottom',
    submenu: [
      {
        title: 'sidebarComponent.generalSettingsTitle',
        route: ORG_GENERAL_SETTINGS,
        requiredFeatures: ['callsEnabled'],
        requiredPermission: WORKSPACE_SETTINGS,
        allowSuperAdmin: true,
      },
      {
        title: 'sidebarComponent.usersSettingsTitle',
        route: ORG_USERS,
        requiredFeatures: [],
        requiredPermission: EDIT_USERS_ONE_TO_ONE,
        allowSuperAdmin: true,
      },
      {
        title: 'sidebarComponent.localizationTitle',
        route: ORG_LOCALIZATION,
        requiredFeatures: ['selfServeTranslationsEnabled'],
        requiredPermission: LOCALIZATION_ONE_TO_ONE,
        allowSuperAdmin: true,
      },
      {
        title: 'sidebarComponent.groupsTitle',
        route: ORG_GROUPS,
        requiredFeatures: ['callsEnabled', 'groupsEnabled'],
        requiredPermission: EDIT_USERS_ONE_TO_ONE,
        requireGroupManagerRole: true,
        allowSuperAdmin: true,
      },
      {
        title: 'sidebarComponent.themesTitle',
        route: ORG_THEMES,
        requiredFeatures: ['themesEnabled', 'callsEnabled'],
        requiredPermission: INTEGRATIONS_ONE_TO_ONE,
        allowSuperAdmin: true,
      },
      {
        title: 'sidebarComponent.callThemingTitle',
        route: ORG_ONE_TO_ONE_THEMES,
        requiredFeatures: ['callsThemingEnabled', 'callsEnabled'],
        requiredPermission: INTEGRATIONS_ONE_TO_ONE,
        allowSuperAdmin: true,
      },
      {
        title: 'sidebarComponent.appIntegrationsTitle',
        route: ORG_INTEGRATIONS,
        requiredFeatures: [],
        requiredPermission: INTEGRATIONS_ONE_TO_ONE,
        allowSuperAdmin: true,
      },
      {
        title: 'sidebarComponent.callBackgroundTitle',
        route: ORG_CALL_BACKGROUND,
        requiredFeatures: ['callsEnabled', 'callBackgroundEnabled'],
        allowSuperAdmin: true,
      },
      {
        title: 'sidebarComponent.callConfigurationsTitle',
        route: ORG_CALL_CONFIGURATIONS,
        requiredFeatures: ['callsEnabled', 'callsConfigurationsEnabled'],
        requiredPermission: CALL_SETTINGS,
        allowSuperAdmin: true,
      },
    ],
  },
];

export default ONE_TO_ONE_ROUTES;
